import { CustomEventIndexOverflowMenu } from 'modules/customEvents/components/CustomEventIndexOverflowMenu';
import { MergedCustomEvent } from 'modules/customEvents/types';
import { configuredIngestionPolicy } from 'modules/customEvents/utils';
import { DATA_SCHEMA_MANAGEMENT_URL } from 'modules/dataDictionary/constants';
import { formatDaysToYears } from 'modules/dataDictionary/utils';
import styled from 'styled-components';
import { SecondaryPageHeader } from 'ui/pageHeader';
import { colors, spacing } from 'ui/theme';
import { FullWidth } from 'ui/utility/fullWidth';
import { DATE_FORMAT_FULL_WITH_HOURS_MINS_SECONDS, displayDate } from 'utils/date';

/**
 * Styles
 */
const HeaderFullWidth = styled(FullWidth)`
  padding-right: ${spacing.layoutLRPadding};
  padding-left: ${spacing.layoutLRPadding};
  margin-bottom: ${spacing.base};
  background-color: ${colors.backgroundSecondary};
`;

const StyledList = styled.div`
  display: flex;
`;

const StyledListItem = styled.span`
  & + & {
    &:before {
      content: '•';
      display: inline-flex;
      justify-content: center;
      min-width: ${spacing.plusHalf};
      color: ${colors.textSecondary};
    }
  }
`;

export const CUSTOM_EVENT_MAPPINGS_PAGE_HEADER_TEXT = {
  breadcrumbContent: 'Custom events',
  breadcrumbHref: DATA_SCHEMA_MANAGEMENT_URL,
  metadataId: 'custom-event-metadata',
};

/**
 * Page Actions
 * Component used in page header to provide actions for page
 */
type PageActionProps = {
  customEvent: MergedCustomEvent | undefined;
};

const PageActions = ({ customEvent }: PageActionProps) => (
  <>
    {!!customEvent && (
      <CustomEventIndexOverflowMenu
        eventName={customEvent.eventName}
        hasReferences={Boolean(customEvent.references)}
        ingestionPolicyFilter={configuredIngestionPolicy(customEvent)}
        isEnabled
      />
    )}
  </>
);

/**
 * Metadata
 * Component used to display details for current custom event
 */
type PageHeaderMetadataProps = {
  lastSeen: number;
  retentionPeriod: number;
  shouldShow: boolean;
};

const PageHeaderMetadata = ({ lastSeen, retentionPeriod, shouldShow }: PageHeaderMetadataProps) => {
  const lastSeenDate = lastSeen
    ? `Last seen ${displayDate(lastSeen, DATE_FORMAT_FULL_WITH_HOURS_MINS_SECONDS)}`
    : 'Never seen';

  const policy =
    retentionPeriod === -1 ? 'Stored forever' : `Stored for ${formatDaysToYears(retentionPeriod)}`;

  return (
    <>
      {shouldShow && (
        <StyledList data-test={CUSTOM_EVENT_MAPPINGS_PAGE_HEADER_TEXT.metadataId}>
          <StyledListItem>{lastSeenDate}</StyledListItem>
          <StyledListItem>{policy}</StyledListItem>
        </StyledList>
      )}
    </>
  );
};

type Props = {
  currentCustomEvent: MergedCustomEvent | undefined;
  customEventName: string;
};

export const CustomEventPageHeader = ({ currentCustomEvent, customEventName }: Props) => (
  <HeaderFullWidth>
    <SecondaryPageHeader
      breadcrumb={{
        content: CUSTOM_EVENT_MAPPINGS_PAGE_HEADER_TEXT.breadcrumbContent,
        href: CUSTOM_EVENT_MAPPINGS_PAGE_HEADER_TEXT.breadcrumbHref,
      }}
      pageActions={<PageActions customEvent={currentCustomEvent} />}
      pageSubSection={
        <PageHeaderMetadata
          lastSeen={currentCustomEvent?.lastSeen || 0}
          retentionPeriod={currentCustomEvent?.retentionPolicy || -1}
          shouldShow={!!currentCustomEvent}
        />
      }
    >
      {customEventName}
    </SecondaryPageHeader>
  </HeaderFullWidth>
);
